import { useState } from "react";
import { Icon } from "@iconify/react";
import { SlideOver } from "@/shared/components/dialog";
import { CalendarDateField } from "@/shared/components/CalendarDateField";
import { Switch } from "@/shared/components/switch";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { deriveNameFromEmail } from "../lib/familyUtils";
import { CATEGORY_ICONS } from "./InviteCaregiverModal";
import { revokeCaregiverAccessApi, updateCaregiverAccessApi } from "@/portals/patient/api/familyApi";
import { CAREGIVER_CATEGORY_OPTIONS, type CaregiverAccess } from "@/shared/types/family";

interface ManageCaregiverAccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caregiver: CaregiverAccess;
  patientName: string;
  /** Refetches the caregivers list from the backend — called after a successful save or revoke. */
  onChange: () => void;
}

type DurationMode = "end-date" | "no-expiration";

function getInitials(name: string): string {
  return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p.charAt(0)).join("").toUpperCase();
}

function formatMonthYear(dateString: string): string {
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return dateString;
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(date);
}

function formatDate(dateString: string): string {
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return dateString;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

/**
 * Direct patient edit of an existing caregiver grant — no request/approval
 * step (per the resolved design: caregivers can separately ask for more
 * categories, but changing what's actually shared is always the patient's
 * own action).
 */
export function ManageCaregiverAccessModal({ open, onOpenChange, caregiver, patientName, onChange }: ManageCaregiverAccessModalProps) {
  const initialEnabled = Object.fromEntries(CAREGIVER_CATEGORY_OPTIONS.map((o) => [o.value, caregiver.categories.includes(o.label)]));
  const [enabledCategories, setEnabledCategories] = useState<Record<string, boolean>>(initialEnabled);
  const allCategoriesSelected = CAREGIVER_CATEGORY_OPTIONS.every((o) => enabledCategories[o.value]);
  const toggleSelectAllCategories = () => {
    setEnabledCategories(
      allCategoriesSelected ? {} : Object.fromEntries(CAREGIVER_CATEGORY_OPTIONS.map((o) => [o.value, true])),
    );
  };
  const [durationMode, setDurationMode] = useState<DurationMode>(caregiver.expiresDate ? "end-date" : "no-expiration");
  const [expiresDate, setExpiresDate] = useState(caregiver.expiresDate ?? "");
  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const isBusy = isSaving || isRevoking;
  const displayName = caregiver.name ?? deriveNameFromEmail(caregiver.email);
  // Reusing this same modal to re-grant a previously-revoked caregiver —
  // the backend now accepts update_caregiver_access on a REVOKED
  // relationship too (see FamilyService.update_caregiver_access), flipping
  // it back to ACTIVE. Nothing to revoke yet in that state, so the danger
  // zone doesn't apply, and the primary action reads as granting access
  // rather than saving a tweak to an already-active grant.
  const isReactivating = caregiver.status === "revoked";

  const close = () => onOpenChange(false);

  const handleSave = async () => {
    const selectedCategoryValues = CAREGIVER_CATEGORY_OPTIONS.filter((o) => enabledCategories[o.value]).map((o) => o.value);
    if (selectedCategoryValues.length === 0) {
      toastError("Select at least one category to share.");
      return;
    }
    const resolvedExpiresDate = durationMode === "end-date" ? expiresDate : "";

    setIsSaving(true);
    const { error } = await updateCaregiverAccessApi(caregiver.id, {
      categories: selectedCategoryValues,
      expiresAt: resolvedExpiresDate ? new Date(`${resolvedExpiresDate}T23:59:59`).toISOString() : undefined,
    });
    setIsSaving(false);

    if (error) {
      toastError(error);
      return;
    }

    onChange();
    toastSuccess(isReactivating ? `${displayName} has been given access again` : `${displayName}'s access has been updated`);
    close();
  };

  const handleRevoke = async () => {
    setIsRevoking(true);
    const { error } = await revokeCaregiverAccessApi(caregiver.id);
    setIsRevoking(false);

    if (error) {
      toastError(error);
      return;
    }

    onChange();
    toastSuccess(`${displayName}'s access has been revoked`);
    close();
  };

  return (
    <SlideOver open={open} onOpenChange={close} width={480}>
      <div className="p-7 pb-28">
        <div className="mb-1 flex items-center gap-1.5 pr-10">
          <span className="h-1.5 w-1.5 rounded-full bg-[#E4002B]" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-[#E4002B]">Family &amp; Caregivers</span>
        </div>
        <h2 className="text-xl font-extrabold text-[#0A2947]">Caregiver Access</h2>
        <p className="mt-1 text-sm text-[#6B7280]">
          Choose what {displayName.split(" ")[0]} can see and for how long. You can change or revoke this anytime.
        </p>

        <div className="my-5 h-px bg-[#E5E7EB]" />

        <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3.5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#111827] text-xs font-bold !text-white">
              {getInitials(displayName)}
            </div>
            <div>
              <p className="text-sm font-extrabold text-[#0A2947]">{displayName}</p>
              <p className="text-xs text-[#6B7280]">{caregiver.email}</p>
            </div>
          </div>
          {caregiver.grantedDate && (
            <p className="shrink-0 text-right text-xs text-[#9CA3AF]">
              Caregiver since <span className="font-semibold text-[#374151]">{formatMonthYear(caregiver.grantedDate)}</span>
            </p>
          )}
        </div>

        <div className="mt-5 overflow-hidden rounded-2xl border border-[#E5E7EB]">
          <div className="flex items-center justify-between gap-3 border-b border-[#E5E7EB] px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">Shared Categories</p>
            <button
              type="button"
              onClick={toggleSelectAllCategories}
              className="shrink-0 text-xs font-bold text-[#E4002B] hover:underline"
            >
              {allCategoriesSelected ? "Deselect All" : "Select All"}
            </button>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {CAREGIVER_CATEGORY_OPTIONS.map((category) => {
              const enabled = Boolean(enabledCategories[category.value]);
              return (
                <div
                  key={category.value}
                  className={cn(
                    "flex items-center justify-between gap-3 border-l-4 px-4 py-3",
                    enabled ? "border-[#F36F8F] bg-[#FFF0F3]" : "border-transparent",
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", enabled ? "bg-[#F36F8F]" : "bg-[#F3F4F6]")}>
                      <Icon icon={CATEGORY_ICONS[category.value]} className={cn("h-4 w-4", enabled ? "text-white" : "text-[#6B7280]")} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#0A2947]">{category.label}</p>
                      <p className="text-xs text-[#9CA3AF]">{category.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={() =>
                      setEnabledCategories((current) => ({ ...current, [category.value]: !current[category.value] }))
                    }
                  />
                </div>
              );
            })}
          </div>
          <div className="flex items-start gap-1.5 border-t border-[#E5E7EB] bg-[#F9FAFB] px-4 py-2.5 text-[11px] text-[#9CA3AF]">
            <Icon icon="lucide:lock" className="mt-0.5 h-3 w-3 shrink-0" />
            Access is read-only. Caregivers can never upload, edit, or delete records.
          </div>
        </div>

        {isEditingDuration ? (
          <div className="mt-5">
            <div className="mb-2 flex items-center gap-2">
              <Icon icon="lucide:calendar" className="h-4 w-4 text-[#E4002B]" />
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">Access Duration</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div
                onClick={() => setDurationMode("end-date")}
                className={cn(
                  "cursor-pointer rounded-xl border p-3",
                  durationMode === "end-date" ? "border-[#F36F8F] bg-[#FFF0F3]" : "border-[#E5E7EB]",
                )}
              >
                <p className="mb-1 text-xs font-bold text-[#0A2947]">Set an end date</p>
                {durationMode === "end-date" ? (
                  <div onClick={(event) => event.stopPropagation()}>
                    <CalendarDateField value={expiresDate} onChange={setExpiresDate} disablePast placeholder="Select expiration date" />
                  </div>
                ) : (
                  <p className="text-[11px] text-[#9CA3AF]">Access expires automatically.</p>
                )}
              </div>
              <div
                onClick={() => setDurationMode("no-expiration")}
                className={cn(
                  "cursor-pointer rounded-xl border p-3",
                  durationMode === "no-expiration" ? "border-[#F36F8F] bg-[#FFF0F3]" : "border-[#E5E7EB]",
                )}
              >
                <p className="mb-1 text-xs font-bold text-[#0A2947]">No expiration</p>
                <p className="text-[11px] text-[#9CA3AF]">Active until you revoke it manually.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-[#E5E7EB] px-4 py-3.5">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FFF0F3]">
                <Icon icon="lucide:calendar" className="h-4 w-4 text-[#E4002B]" />
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">Access Expires</p>
                <p className="text-sm font-semibold text-[#0A2947]">
                  {durationMode === "end-date" && expiresDate ? formatDate(expiresDate) : "No expiration"}
                </p>
              </div>
            </div>
            <Button
              variant="link"
              onClick={() => setIsEditingDuration(true)}
              className="!p-0 !text-xs !font-bold !uppercase !text-[#E4002B] hover:!underline"
            >
              Change
            </Button>
          </div>
        )}

        {!isReactivating && (
          <div className="mt-5">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">Danger Zone</p>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] p-4">
              <div>
                <p className="text-sm font-bold text-[#B91C1C]">Danger Zone</p>
                <p className="mt-0.5 max-w-xs text-xs leading-relaxed text-[#7F1D1D]">
                  Revoking access immediately removes {displayName}'s ability to view any of{patientName ? ` ${patientName}'s` : " your"} records.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleRevoke}
                disabled={isBusy}
                loading={isRevoking}
                className="!shrink-0 !rounded-lg !border-[#E4002B] !px-4 !py-2 !text-xs !font-semibold !text-[#E4002B] hover:!border-[#E4002B] hover:!bg-white hover:!text-[#E4002B]"
              >
                Revoke Access
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 flex justify-between gap-3 border-t border-[#E5E7EB] bg-white px-7 py-4">
        <Button variant="link" onClick={close} disabled={isBusy} className="!p-0 !text-sm !font-semibold !text-[#6B7280] hover:!text-[#0A2947]">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isBusy}
          loading={isSaving}
          className="!rounded-lg !bg-[#E4002B]/50 !px-5 !py-2.5 !text-sm !font-semibold !text-white hover:!bg-[#E4002B]/60"
        >
          {!isSaving && <Icon icon={isReactivating ? "lucide:unlock" : "lucide:save"} className="h-4 w-4" />}
          {isReactivating ? "Give Access" : "Save Changes"}
        </Button>
      </div>
    </SlideOver>
  );
}
